import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { fmtQty, stockTone } from "../lib/stock";

const MODES = [
  { v: "add", label: "Add stock" },
  { v: "remove", label: "Remove" },
  { v: "set", label: "Set exact" },
];

const reasonLabel = (r) => {
  if (!r) return "";
  if (r === "order") return "Order placed";
  if (r === "order_edit") return "Order edited";
  if (r === "order_cancel") return "Order cancelled";
  if (r === "order_restore") return "Order restored";
  return r.replace(/^manual_(add|remove|set)/, (_, m) => ({ add: "Stock added", remove: "Stock removed", set: "Stock set" })[m]);
};

const MovementList = ({ moves }) => (
  <div className="max-h-48 overflow-y-auto divide-y divide-[color:var(--border)] text-xs" data-testid="stock-movements-list">
    {moves.length === 0 && <div className="py-3 text-[color:var(--muted)]">No stock movements yet.</div>}
    {moves.map((m) => (
      <div key={m.id} className="py-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate">{reasonLabel(m.reason)}</div>
          <div className="text-[color:var(--muted)]">{new Date(m.created_at).toLocaleString("en-IN")} · {m.user_name}</div>
        </div>
        <div className="text-right tabular-nums shrink-0">
          <div className={m.delta < 0 ? "text-[color:var(--primary)]" : "text-[color:var(--moss)]"}>
            {m.delta > 0 ? "+" : ""}{fmtQty(m.delta)}
          </div>
          <div className="text-[color:var(--muted)]">bal {fmtQty(m.balance)}</div>
        </div>
      </div>
    ))}
  </div>
);

export const StockDialog = ({ product, onClose, onSaved }) => {
  const [mode, setMode] = useState("add");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [moves, setMoves] = useState([]);

  useEffect(() => {
    if (!product) return;
    setMode("add");
    setQty("");
    setNote("");
    setMoves([]);
    api.get("/stock/movements", { params: { product_id: product.id, limit: 50 } }).then((r) => setMoves(r.data)).catch(() => setMoves([]));
  }, [product]);

  const save = async () => {
    const v = Number(qty);
    if (qty === "" || Number.isNaN(v) || v < 0) return toast.error("Enter a valid quantity");
    setSaving(true);
    try {
      const { data } = await api.post(`/products/${product.id}/stock`, { mode, quantity: v, note: note || null });
      toast.success(`Stock updated · ${fmtQty(data.stock_qty)} ${product.unit || "units"}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stock-dialog">
        <DialogHeader>
          <DialogTitle className="font-tamil">Stock · {product?.name}</DialogTitle>
        </DialogHeader>
        {product && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[color:var(--muted)]">Available now</span>
              <span className={`px-2.5 py-1 rounded-full text-sm font-bold tabular-nums ${stockTone(product)}`} data-testid="stock-dialog-current">
                {fmtQty(product.stock_qty)} {product.unit || ""}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setMode(m.v)}
                  className={`h-10 rounded-lg text-sm font-semibold border transition-colors ${mode === m.v ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]" : "bg-white border-[color:var(--border)] hover:bg-[color:var(--surface-2)]"}`}
                  data-testid={`stock-mode-${m.v}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div>
              <Label>Quantity ({product.unit || "units"})</Label>
              <Input type="number" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 font-mono" autoFocus data-testid="stock-qty-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Received from factory" data-testid="stock-note-input" />
            </div>
            <div>
              <p className="overline mb-1">Recent movements</p>
              <MovementList moves={moves} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="stock-save-button">
                {saving ? "Saving…" : "Save stock"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StockDialog;
